import { z, ZodObject } from "zod";
import { ZodRawShape } from "zod/lib/types";
import retry from "async-retry";
import { TimeoutsOptions } from "retry";

class Failure {
  private constructor(
    readonly error: Error,
    readonly metadata?: Record<string, any>
  ) {}
  static new({
    error, metadata
  }: {
    error: Error, metadata?: Record<string, any>
  }) {
    return new Failure(error, metadata);
  }
}

export type FetchJSONResult<T> =
  | { data: T, failure: null, res: Response }
  | { data: null, failure: Failure, res: Response };

export async function fetchJson<
  T = any,
>(
  url: string | URL,
  options?: RequestInit | null,
  schema?: undefined
): Promise<FetchJSONResult<T>>;

export async function fetchJson<
  Z extends ZodRawShape = any,
>(
  url: string | URL,
  options: RequestInit | null,
  schema: ZodObject<Z>
): Promise<FetchJSONResult<z.infer<ZodObject<Z>>>>;

export async function fetchJson<
  T = any,
  Z extends ZodRawShape = any,
>(
  url: string | URL,
  options: RequestInit | null = {},
  schema?: ZodObject<Z>,
  timeoutOptions: TimeoutsOptions = {
    retries: 3, // Increase max retries
    factor: 1.5, // Exponential factor
    minTimeout: 1000, // Start with 1 second
    maxTimeout: 64000 // Max delay of 64 seconds
  }
): Promise<FetchJSONResult<T>> {
  const urlString = typeof url === "string" ? url : url.toString();
  if (urlString.includes("/api/command")) {
    console.log("fetchJson", url, options);
  }

  const res = await retry(() => (
    fetch(url, options ?? undefined)
  ), timeoutOptions);

  // fs.writeFileSync(path.resolve(appRootPath.path, "logs/mcp-res.log"), `${res.status} ${res.statusText}\n`);
  let json: unknown = null;
  try {
    json = await res.json();
  } catch (e) {
    // fs.writeFileSync(path.resolve(appRootPath.path, "logs/mcp-json.log"), `${res.status} ${res.status}\n`);
  }
  if (!res.ok) {
    let errorText: string = "";
    if ((json as any)?.error) {
      errorText = (json as any).error;
    } else {
      errorText = `${ res.status } ${ res.statusText }`;
    }
    return { data: null, failure: Failure.new({
      error: new Error(`Failed to fetch JSON: ${ errorText }`),
      metadata: { url, options, json }
    }), res };
  }
  if (schema) {
    const {
      data, error, success
    } = await schema.safeParseAsync(json);
    if (!success || error || !data) {
      return { data: null, failure: Failure.new({
        error: new Error(`Failed to parse JSON: ${ error ?? "No data" }`),
        metadata: { url, options, json }
      }), res };
    }
    return { data: data as T, failure: null, res };
  }
  return { data: json as T, failure: null, res };
}